import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon, ChevronDownIcon, Loader2Icon, SendIcon } from 'lucide-react';
import { FormField } from './FormField';
import { interestOptions } from '../../data/site';
import { useContactForm } from '../../hooks/useContactForm';
import type { Interest } from '../../types/contact';
type ContactFormProps = {
  interest: Interest | '';
  onInterestChange: (value: Interest | '') => void;
};
const inputClass = 'block w-full rounded-xl border border-brand-slate/20 bg-white px-4 py-[clamp(0.75rem,0.65rem+0.3vw,1.1rem)] text-body-fluid text-brand-slate placeholder:text-brand-gray/70 transition-[border-color,box-shadow] duration-150 ease-out hover:border-brand-slate/35 focus:border-brand-blue focus:outline-none focus:ring-4 focus:ring-brand-accent/25 aria-[invalid=true]:border-[#b42318]';
const EASE = [0.23, 1, 0.32, 1] as const;
export function ContactForm({
  interest,
  onInterestChange
}: ContactFormProps) {
  const {
    fields,
    errors,
    status,
    update,
    updateInterest,
    submit,
    reset
  } = useContactForm(interest, onInterestChange);
  const submitting = status === 'submitting';
  return <div className="radius-fluid bg-white pad-box shadow-[0_30px_80px_-30px_rgba(0,0,0,0.45)]" data-id="element-211" data-mp="1bt8rleorrfoj">
      <AnimatePresence mode="wait" initial={false} data-id="element-212" data-mp="171h1145m59ye">
        {status === 'success' ? <motion.div key="success" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -12
      }} transition={{
        duration: 0.25,
        ease: EASE
      }} role="status" className="flex min-h-[24rem] flex-col items-start justify-center" data-id="element-213" data-mp="1kt5dj8jry0fw">
            <CheckCircle2Icon className="h-12 w-12 text-brand-blue" aria-hidden="true" data-id="element-214" data-mp="1pt1kngnrq7zo" />
            <h3 className="mt-6 text-h3 font-extrabold text-brand-navy" data-id="element-215" data-mp="ed8ngj3as26">
              Got it{fields.name ? `, ${fields.name.split(' ')[0]}` : ''}. Message received.
            </h3>
            <p className="mt-3 max-w-[42ch] text-body-fluid" data-id="element-216" data-mp="l3o5dc39gjvu">
              David reads every message himself. Expect a reply soon — or call {''}
              <a href="tel:+17025096825" className="font-semibold text-brand-blue underline-offset-4 hover:underline" data-id="element-217" data-mp="p619jw2d09qi">
                (702) 509-6825
              </a>{' '}
              if it can’t wait.
            </p>
            <button type="button" onClick={reset} className="btn mt-8 border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white" data-id="element-218" data-mp="141qmz5thbejm">
              Send another message
            </button>
          </motion.div> : <motion.form key="form" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -12
      }} transition={{
        duration: 0.25,
        ease: EASE
      }} onSubmit={submit} noValidate aria-label="Contact David" className="grid gap-x-[clamp(1rem,1.6vw,2rem)] gap-y-[clamp(1.1rem,1.6vw,1.75rem)] sm:grid-cols-2" data-id="element-219" data-mp="1hsm0ivexajtj">
            <FormField id="contact-name" label="Name" error={errors.name} data-id="element-220" data-mp="3797oulfrpw74">
              <input id="contact-name" name="name" autoComplete="name" value={fields.name} onChange={e => update('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'contact-name-error' : undefined} className={inputClass} data-id="element-221" data-mp="32a4o3d6tmc4y" />
            </FormField>
            <FormField id="contact-email" label="Email" error={errors.email} data-id="element-222" data-mp="3l87dht3a6b3a">
              <input id="contact-email" name="email" type="email" autoComplete="email" value={fields.email} onChange={e => update('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'contact-email-error' : undefined} className={inputClass} data-id="element-223" data-mp="3g9nqrqgv4v9v" />
            </FormField>
            <FormField id="contact-phone" label="Phone" optional error={errors.phone} data-id="element-224" data-mp="3jjfdg30tb6ri">
              <input id="contact-phone" name="phone" type="tel" autoComplete="tel" value={fields.phone} onChange={e => update('phone', e.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'contact-phone-error' : undefined} className={inputClass} data-id="element-225" data-mp="3oia0jh5tbhhf" />
            </FormField>
            <FormField id="contact-interest" label="I’m interested in" error={errors.interest} data-id="element-226" data-mp="b9xb4inqnz1w">
              <div className="relative" data-id="element-227" data-mp="yzzy4kcsqetn">
                <select id="contact-interest" name="interest" value={interest} onChange={e => updateInterest(e.target.value as Interest | '')} aria-invalid={Boolean(errors.interest)} aria-describedby={errors.interest ? 'contact-interest-error' : undefined} className={`${inputClass} appearance-none pr-11`} data-id="element-228" data-mp="13rrha9tmhi9r">
                  <option value="" data-id="element-229" data-mp="2t9yeyvlfyi59">Choose one…</option>
                  {interestOptions.map(option => <option key={option} value={option} data-id="element-230" data-mp="25lold7hzkq3k">
                      {option}
                    </option>)}
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-brand-slate/60" aria-hidden="true" data-id="element-231" data-mp="2elbnjvijhzxo" />
              </div>
            </FormField>
            <FormField id="contact-message" label="Message" error={errors.message} className="sm:col-span-2" data-id="element-232" data-mp="3q1a56nrs9uai">
              <textarea id="contact-message" name="message" rows={5} value={fields.message} onChange={e => update('message', e.target.value)} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'contact-message-error' : undefined} className={`${inputClass} resize-y`} data-id="element-233" data-mp="2imwvpnu95jzy" />
            </FormField>

            {status === 'error' && <p role="alert" className="flex items-center gap-2 text-sm font-medium text-[#b42318] sm:col-span-2" data-id="element-234" data-mp="24vjgun7ahab9">
                <AlertCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-235" data-mp="28wleizlk5eb4" />
                Something went wrong sending that. Please try again or email Results@DavidMoceri.com.
              </p>}

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 sm:col-span-2" data-id="element-236" data-mp="1h4w51qu960jf">
              <button type="submit" disabled={submitting} className="btn bg-brand-navy text-white hover:bg-brand-blue disabled:cursor-wait disabled:opacity-80" data-id="element-237" data-mp="1l6r4sqwjc5v3">
                {submitting ? <>
                    <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-238" data-mp="14n38xuzbwm0y" />
                    Sending…
                  </> : <>
                    Send It
                    <SendIcon className="h-[1.05em] w-[1.05em]" aria-hidden="true" data-id="element-239" data-mp="upp4yuuldppj" />
                  </>}
              </button>
              <p className="text-sm text-brand-gray" data-id="element-240" data-mp="pwvvxus3bv7n">No spam. Your info stays with David.</p>
            </div>
          </motion.form>}
      </AnimatePresence>
    </div>;
}